/** Series detail = the Studio conversation (PRD §30), with the episode/version index folded above it. */
import { useState } from "react";
import { Link } from "../../app/router";
import type { SeriesDetail } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, QueryView } from "../../components/ui";
import { mergeMessages, useApi, useCache, useConversation, useMutation } from "../../data/hooks";
import { useSeries, useSeriesPlaylists } from "../../data/queries";
import { dateTime, languageName } from "../../lib/format";
import { SERIES_STATUS, SESSION_STATE, VERSION_STATUS } from "../../lib/status";
import type { Message } from "../../api/types";
import { Composer } from "./Composer";
import { playlistImageText, SeriesCoverPicker } from "./SeriesCover";
import { Conversation, type PendingMessage } from "./Conversation";

export function SeriesScreen({ seriesId }: { seriesId: string }) {
  const q = useSeries(seriesId);
  return <div className="series-screen"><QueryView q={q}>{(d) => <SeriesBody d={d} />}</QueryView></div>;
}

function SeriesBody({ d }: { d: SeriesDetail }) {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const conv = useConversation(d.series.id);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const s = d.series;
  const st = SERIES_STATUS[s.status];
  const ss = SESSION_STATE[d.session.state];
  const stop = useMutation(() => api.interrupt(s.id), { invalidate: [`series/${s.id}`, `messages/${s.id}`], onSuccess: () => toast.ok("Stop sent to the Studio."), onError: (e) => toast.error(e, "Couldn't stop") });

  const send = async (body: string) => {
    const key = `p-${Date.now()}`;
    setPending((p) => [...p, { key, body }]);
    try {
      const m = await api.sendMessage(s.id, body);
      cache.setData<Message[]>(`messages/${s.id}`, (prev) => mergeMessages(prev, [m]));
      setPending((p) => p.filter((x) => x.key !== key));
    } catch (e) {
      setPending((p) => p.map((x) => (x.key === key ? { ...x, failed: true } : x)));
      toast.show({ tone: "danger", text: "Message not sent.", action: { label: "Retry", run: () => { setPending((p) => p.filter((x) => x.key !== key)); void send(body); } } });
      void e;
    }
  };

  const busyHint = d.session.state === "active" ? "The Studio is mid-step — your message is delivered at its next turn."
    : d.session.state === "usage_limited" ? `Usage limit reached${s.usage_resets_at ? ` — the Studio resumes ${dateTime(s.usage_resets_at)}` : ""}. Messages wait until then.`
    : d.session.state === "takeover" ? "The session is taken over in Claude Code. Messages are delivered when it's released." : undefined;

  const openDecisions = d.decisions.filter((x) => x.status === "open");
  return (
    <>
      <header className="series-head">
        <Link to="/studio" className="back-link"><Icon name="back" size={18} /> Studio</Link>
        <h1>{s.title}</h1>
        <div className="series-head-meta">
          <Badge tone={st.tone}>{st.label}</Badge>
          <Badge tone={ss.tone} dot>{ss.label}</Badge>
          {s.priority === "urgent" && <Badge tone="danger">Urgent</Badge>}
          <span className="muted small">{[s.category, s.languages.map(languageName).join(", "), s.source?.title].filter(Boolean).join(" · ")}</span>
          {d.episodes.some((e) => e.versions.some((v) => v.status === "approved")) && (
            <Link to={`/publish/schedule?series=${encodeURIComponent(s.id)}`} className="btn btn-secondary btn-sm push"><Icon name="calendar" size={18} /><span>Schedule series</span></Link>
          )}
        </div>
        <SeriesPlaylists seriesId={s.id} />
        {s.stage_detail && <p className="series-stage"><span className={`activity-dot ${d.session.state === "active" ? "is-live" : ""}`} aria-hidden /> {s.stage_detail}</p>}
        {d.episodes.length > 0 && (
          <details className="episodes-index">
            <summary><Icon name="chevronDown" size={18} /> Episodes ({d.episodes.length}){openDecisions.length ? <Badge tone="attention">{openDecisions.length} open decision{openDecisions.length > 1 ? "s" : ""}</Badge> : null}</summary>
            <ol>
              {d.episodes.map((e) => (
                <li key={e.id}>
                  <span className="ep-title"><span className="muted">{e.sequence}.</span> {e.title}</span>
                  <span className="ep-versions">
                    {e.versions.length ? e.versions.map((v) => (
                      <Link key={v.id} to={`/review/${v.id}`} className={`ver-chip tone-${VERSION_STATUS[v.status].tone}`} aria-label={`${languageName(v.language_code)} version ${v.version}: ${VERSION_STATUS[v.status].label}`}>
                        {v.language_code.toUpperCase()} v{v.version}
                      </Link>
                    )) : <span className="muted small">{e.status}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </details>
        )}
      </header>
      <QueryView q={conv}>{(messages) => <Conversation detail={d} messages={messages} pending={pending} />}</QueryView>
      <Composer onSend={(b) => void send(b)} onStop={() => stop.mutate()} canStop={d.session.state === "active"} stopping={stop.pending} busyHint={busyHint} />
    </>
  );
}

/**
 * M2.2: the series' playlist on each account it is published to — a link, how many episodes are in it, and "Sync playlist".
 * M2.4: the series cover (its image on every channel) and each playlist's image state.
 */
function SeriesPlaylists({ seriesId }: { seriesId: string }) {
  const api = useApi();
  const toast = useToast();
  const q = useSeriesPlaylists(seriesId);
  const sync = useMutation((accountId: string) => api.syncSeriesPlaylist(seriesId, accountId), {
    invalidate: [`publications/series-playlists/${seriesId}`, "publications", "publication"],
    onSuccess: (r) => {
      const items = r.results.flatMap((x) => x.items);
      const added = items.filter((i) => i.outcome === "added").length;
      const failed = items.filter((i) => i.outcome === "retry" || i.outcome === "failed");
      if (failed.length) toast.show({ tone: "info", text: `Playlist synced; ${failed.length} episode${failed.length > 1 ? "s" : ""} could not be added yet (${failed[0]!.message ?? "error"}). It retries on its own.` });
      else toast.ok(added ? `Added ${added} episode${added > 1 ? "s" : ""} to the playlist.` : "The playlist is up to date.");
    },
    onError: (e) => toast.error(e, "Couldn't sync the playlist"),
  });
  const rows = q.data?.playlists ?? [];
  const playlistsKey = `publications/series-playlists/${seriesId}`;
  return (
    <>
    <SeriesCoverPicker seriesId={seriesId} invalidate={[playlistsKey]} />
    {rows.length > 0 && (
    <ul className="series-playlists" aria-label="Series playlists">
      {rows.map((p) => {
        const img = playlistImageText(p.image);
        return (
        <li key={p.account_id} className="list-row">
          <span className="list-main">
            <span className="list-title">
              {p.url ? <a className="text-link" href={p.url} target="_blank" rel="noreferrer">Playlist «{p.title}»</a> : <>Playlist «{p.title}» (not created yet)</>}
            </span>
            <span className="list-sub">
              {p.account_name} · {p.episodes_in_playlist} of {p.episodes_uploaded} uploaded episode{p.episodes_uploaded === 1 ? "" : "s"} · {p.privacy}
              {p.last_error ? ` · last problem: ${p.last_error.message}` : ""}
            </span>
            {img && (
              <span className={`list-sub playlist-image tone-${img.tone}`}>
                {img.text}{img.detail ? ` — ${img.detail}` : ""}
              </span>
            )}
          </span>
          <Button size="sm" variant="ghost" icon="refresh" busy={sync.pending} disabled={!p.episodes_uploaded} onClick={() => void sync.mutate(p.account_id)}>Sync playlist</Button>
        </li>
        );
      })}
    </ul>
    )}
    </>
  );
}
